<template>
  <div>
    <div
      v-if="wasEnrolledInLoyalty"
      class="section center animation-slide-left"
    >
      <span class="icon icon--block icon--medium secondary fa-check-circle secondary" />
      <h2 class="title title--3">{{ content.LoyaltyEnrollmentSuccess.title }}</h2>

      <p>{{ content.LoyaltyEnrollmentSuccess.description }}</p>

      <button
        class="button button--large button--primary button--auto-width"
        type="button"
        @click="$closeModal('createAccount')"
      >
        Continue
      </button>
    </div>

    <template v-else>
      <div class="section center">
        <info-message
          v-if="shouldDisplayClaimPointsBanner"
          class="spacing"
          :message="giftedPointsInfoBannerContent"
          icon="icon far fa-gift"
        />

        <create-account-header />
      </div>

      <validated-form
        v-if="showingEmailCreateAccountForm"
        :class="['section section--alt-background', { 'animation-slide-left': merchant.thirdPartySignIn.enabled }]"
        name="createAccount"
        @valid-submit="formSubmitHandler"
      >
        <full-name-input v-model="fullName" />

        <text-input
          v-if="userValidations.email"
          v-model="email"
          type="email"
          label="Email"
          name="emailCreateAccount"
          autocomplete="email"
          :show-as-optional="true"
          :validations="{
            required: userValidations.email.isRequired
          }"
        />

        <text-input
          v-if="userValidations.primarySmsNumber"
          v-model="phoneNumber"
          type="phone"
          label="Phone Number"
          name="phoneCreateAccount"
          autocomplete="tel"
          :show-as-optional="true"
          :validations="{
            required: userValidations.primarySmsNumber.isRequired
          }"
        />

        <text-input
          v-if="userValidations.favoriteStoreId"
          type="text-as-button"
          label="Favorite Store"
          name="favoriteStore"
          icon-class="fa-map-marker-alt"
          autocomplete="off"
          :model-value="favoriteStore && favoriteStore.description"
          :show-as-optional="!userValidations.favoriteStoreId.isRequired"
          :validations="{
            required: userValidations.favoriteStoreId.isRequired
          }"
          @click="openFavoriteStoreModal"
        />

        <text-input
          v-if="userValidations.zip"
          v-model="zip"
          type="text"
          label="Postal Code"
          name="zip"
          mask="postalCode"
          icon-class="fa-map-marker-alt"
          inputmode="numeric"
          autocomplete="postal-code"
          :show-as-optional="true"
          :validations="{
            required: userValidations.zip.isRequired,
            postal_code: true
          }"
        />

        <birthday-input
          v-if="userValidations.birthdayInfo"
          v-model="dateOfBirth"
          :include-year="!!userValidations['birthdayInfo.year']"
          :required="userValidations.birthdayInfo.isRequired"
        />

        <text-input
          v-if="userValidations.password"
          v-model="password"
          type="password"
          label="Password"
          name="passwordCreateAccount"
          :show-as-optional="true"
          :validations="{
            required: userValidations.password.isRequired,
            strong_password: true
          }"
        />

        <text-input
          v-if="userValidations.inviteCode"
          v-model="inviteCode"
          type="text"
          label="Invite Code"
          name="inviteCode"
          :show-as-optional="true"
          :validations="{
            required: userValidations.inviteCode.isRequired,
            alpha_num: true,
          }"
        />

        <template v-if="userValidations.smsNotificationCheckbox">
          <switch-input
            v-model="smsOptIn"
            :show-info-icon="!showSmsDisclaimer"
            :description="smsTermsContent"
            label="Get Text Updates"
            name="smsOptIn"
          />

          <p
            v-if="showSmsDisclaimer"
            class="content smaller"
            v-html="smsTermsContent"
          />

          <hr class="no-spacing-top">

        </template>

        <checkbox-input
          v-model="termsAccepted"
          label="Terms of Use"
          name="termsAccepted"
          :validations="{
            required: true
          }"
        >
          I agree to the

          <button
            class="link"
            type="button"
            @click="$openModal('termsAndConditions')"
          >
            Terms & Conditions
          </button>
        </checkbox-input>

        <p
          v-if="enrollmentOptions.marketingOptIn"
          class="content smaller"
          v-html="dynamicContent.CreateAccount.metadata.termsAndConditions"
        />

        <div class="center">
          <button
            :class="['button button--primary button--large', { 'button--loading': isLoading }]"
            type="submit"
          >
            {{ dynamicContent.CreateAccount.metadata.buttonCta }}
          </button>
        </div>
      </validated-form>

      <div
        v-else
        class="section"
      >
        <div class="container max-width-social-sign-in">
          <button
            class="button button--primary button--large"
            type="button"
            @click="showingEmailCreateAccountForm = true"
          >
            Sign Up With Email
          </button>

          <div class="title-separator">
            <p class="title title--6 uppercase">
              or
            </p>
          </div>

          <sign-in-with-google
            v-if="merchant.thirdPartySignIn.google"
            type="sign-up"
          />
          <sign-in-with-facebook v-if="merchant.thirdPartySignIn.facebook" />
        </div>
      </div>

      <div class="section section--alt-background section--half-padding center">
        <p>
          Already have an account?
          <button
            class="link primary"
            type="button"
            @click="openSignInModal"
          >
            Sign In
          </button>
        </p>
      </div>
    </template>
  </div>
</template>


<script>
  import { mapGetters } from 'vuex';

  import errorHandlers from '@/services/error-handlers';
  import events from '@/services/events';

  import parseTwoFactorError from '@/helpers/parse-two-factor-error';


  export default {
    name: 'CreateAccount',

    data() {
      return {
        dateOfBirth: null,
        email: null,
        favoriteStore: null,
        fullName: null,
        isLoading: false,
        overrideWelcomeToLoyalty: false,
        password: null,
        phoneNumber: null,
        showingEmailCreateAccountForm: false,
        smsOptIn: false,
        termsAccepted: true,
        wasEnrolledInLoyalty: false,
        zip: null,
        inviteCode: null
      };
    },

    computed: {
      ...mapGetters('content', ['content', 'dynamicContent']),
      ...mapGetters('merchant', ['enrollmentOptions', 'loyaltyOptions', 'merchant']),
      ...mapGetters('offers', ['loyalty', 'offers']),
      ...mapGetters('session', ['session']),

      shouldDisplayClaimPointsBanner() {
        return !!this.offers.giftingPoints.pointGiftClaimCode;
      },

      giftedPointsInfoBannerContent() {
        const pointsCurrency = this.offers.giftingPoints.currencyPluralName;

        return `Create an account to claim your ${pointsCurrency}!`;
      },

      userValidations() {
        const userValidations = {};

        Object.entries(this.merchant.userSignUpFields).forEach(([signUpField, signUpFieldValues]) => {
          const hasPlatFormType = Object.prototype.hasOwnProperty.call(signUpFieldValues, 'platFormTypes');

          const isDisplayed = (hasPlatFormType && signUpFieldValues.platformTypes.find('Web')) || !hasPlatFormType;

          if (isDisplayed) {
            Object.assign(userValidations, { [signUpField]: { ...signUpFieldValues } });
          }
        });

        return userValidations;
      },

      showSmsDisclaimer() {
        return this.merchant.options.showSmsDisclaimer;
      },

      smsTermsContent() {
        return this.dynamicContent.CreateAccount.metadata.smsTerms;
      }
    },

    mounted() {
      this.onComponentMounted();
    },

    methods: {
      onComponentMounted() {
        const { smsNotificationCheckbox } = this.merchant.userSignUpFields;

        const inviteCode = this.$route.query.referredBy;

        if (inviteCode) this.inviteCode = inviteCode;

        if (!this.merchant.thirdPartySignIn.enabled) {
          this.showingEmailCreateAccountForm = true;
        }

        if (smsNotificationCheckbox && smsNotificationCheckbox.isRequired) {
          this.smsOptIn = true;
        }

        if (this.session.prefillAccountInfo) {
          this.email = this.session.prefillAccountInfo.email;
          this.fullName = this.session.prefillAccountInfo.fullName;
          this.primarySmsNumber = this.session.prefillAccountInfo.primarySmsNumber;
        }

        if (this.offers.promoCode.claimCode) {
          this.overrideWelcomeToLoyalty = true;
        }

        if (this.offers.giftingPoints.pointGiftClaimCode) {
          this.overrideWelcomeToLoyalty = true;

          const mutationPayload = { claimGiftedPointsFlow: 'CREATED_ACCOUNT' };

          this.$store.commit('offers/SET_GIFTING_POINTS_DATA', mutationPayload);
        }
      },

      async formSubmitHandler() {
        this.isLoading = true;

        const dispatchPayload = this.formatCreateAccountPayload();

        const { error } = await this.$store.dispatch('session/createAccount', dispatchPayload);

        if (error) {
          this.handleError(error);
        }
        else {
          this.handleSuccess();
        }

        this.isLoading = false;
      },

      handleError(error) {
        const { requiresAuth, ...rest } = parseTwoFactorError(error, this.email, this.phoneNumber);

        if (requiresAuth) {
          const twoFactorModalPayload = { ...rest, onSuccess: this.handleSuccess };

          this.$openModal('twoFactorAuthModal', twoFactorModalPayload, { isRequired: true });
        }
        else {
          errorHandlers.generic(error);

          // this.$triggerGoogleTrackingEvent('CreateAccountFailure', 'Account');

          this.$triggerNewRelicEvent('CreateAccountFailure', 'Error', 'Create Account Failure');
        }
      },

      async handleSuccess() {
        const { signInRedirectRoute } = this.session;

        const notificationEventPayload = { type: 'success', message: 'Welcome! Please check your email.', clickToDismiss: true };

        events.emit('notification', notificationEventPayload);

        await this.checkForRetroClaims();

        if (!this.overrideWelcomeToLoyalty && this.loyalty.isEnrolled) {
          this.wasEnrolledInLoyalty = true;
        }
        else {
          this.$closeModal('createAccount');
        }

        if (signInRedirectRoute) this.redirect(signInRedirectRoute);

        // this.$triggerGoogleTrackingEvent('CreateAccountSuccess', 'Account');

        this.$triggerNewRelicEvent('CreateAccountSuccess', 'Success', 'Create Account Success');
      },

      async checkForRetroClaims() {
        const { receiptToken } = this.$route.params;

        const retroClaimReceipt = this.offers.retroReceiptToken;

        if (receiptToken || retroClaimReceipt) {
          const dispatchPayload = { type: 'cfReceiptToken', value: receiptToken || retroClaimReceipt };

          await this.$store.dispatch('offers/claimPreviousOrder', dispatchPayload);
        }
      },

      formatCreateAccountPayload() {
        return {
          dateOfBirth: this.dateOfBirth,
          email: this.email,
          enrollInLoyalty: this.loyaltyOptions.loyaltyEnrollmentEnabled,
          favoriteStoreId: this.favoriteStore?.storeId,
          fullName: this.fullName,
          notificationPreferences: {
            email: this.enrollmentOptions.marketingOptIn,
            marketingOptIn: this.enrollmentOptions.marketingOptIn,
            sms: this.smsOptIn
          },
          password: this.password,
          primarySmsNumber: this.phoneNumber,
          zip: this.zip,
          inviteCode: this.inviteCode
        };
      },

      openSignInModal() {
        this.$closeModal('createAccount');

        this.$openModal('signIn');
      },

      selectStore(store) {
        this.favoriteStore = store;
      },

      openFavoriteStoreModal() {
        const selectFavoriteStoreModalPayload = {
          selectedStoreId: this.favoriteStore?.storeId,
          onFavoriteStoreSelection: this.selectStore
        };

        this.$openModal('selectFavoriteStore', selectFavoriteStoreModalPayload);
      },

      redirect(route) {
        const routerPayload = { name: route.name, params: route.params, query: route.query };

        this.$router.replace(routerPayload);
      }
    }
  };
</script>
